/**
 * Product Media Showcase JavaScript
 * Handles video functionality and responsive behavior
 */

class ProductMediaShowcase extends HTMLElement {
  constructor() {
    super();

    this.videos = this.querySelectorAll('video');
    this.externalVideos = this.querySelectorAll('iframe');
    this.mediaContainers = this.querySelectorAll('.product-media-showcase__image-container, .product-media-showcase__video-container');
    this.videoContainers = Array.from(this.querySelectorAll('.product-media-showcase__video-container'));

    // Track the current viewport mode so we only update when it actually changes
    this.isMobileViewport = null;

    // Debug flag to enable logging (disabled by default)
    this.debugMode = false;

    // Handle video errors
    this.setupVideoErrorHandling();

    this.initializeVideos();
    this.setupEventListeners();
  }

  /**
   * Set up error handling for videos
   */
  setupVideoErrorHandling() {
    this.videos.forEach(video => {
      // Handle video loading errors
      video.addEventListener('error', (e) => {
        console.error('Video loading error in product media showcase:', e);
        this.handleVideoError(video);
      });

      // Handle source errors
      const sources = video.querySelectorAll('source');
      sources.forEach(source => {
        source.addEventListener('error', (e) => {
          console.error('Video source error in product media showcase:', e);
          this.handleVideoError(video);
        });
      });
    });
  }

  /**
   * Handle video error by showing fallback or hiding container
   */
  handleVideoError(video) {
    // Try to show fallback image if available
    const fallbackImage = video.querySelector('.product-media-showcase__fallback-image');
    if (fallbackImage) {
      fallbackImage.style.display = 'block';
      video.style.display = 'none';
    }

    // If no fallback image, hide the video container
    if (!fallbackImage && video.parentElement) {
      video.parentElement.classList.add('video-error');
    }
  }

  /**
     * Initialize video elements
     */
  initializeVideos() {
    // Track play promises to avoid conflicts
    this.playPromises = new WeakMap();

    // Log video state if debug mode is enabled (console only)
    this.logVideoState = (video, state, error = null) => {
      // Only log in debug mode
      if (!this.debugMode) return;

      try {
        // Get video ID safely
        let videoId = 'unknown';
        if (video && typeof video === 'object') {
          videoId = video.id || 'unnamed-video';
        }

        // Log to console only
        if (error) {
          console.debug(`Video ${videoId}: ${state}`, error);
        } else {
          console.debug(`Video ${videoId}: ${state}`);
        }
      } catch (err) {
        // Ignore logging errors
      }
    };

    // Handle native videos
    this.videos.forEach(video => {
      // Set poster if available
      if (video.dataset.poster) {
        video.poster = video.dataset.poster;
      }

      // Add event listeners for tracking video states
      video.addEventListener('loadstart', () => this.logVideoState(video, 'LOADING STARTED'));
      video.addEventListener('loadeddata', () => this.logVideoState(video, 'LOADING COMPLETE'));
      video.addEventListener('canplay', () => this.logVideoState(video, 'CAN PLAY'));
      video.addEventListener('playing', () => this.logVideoState(video, 'PLAYING'));
      video.addEventListener('pause', () => this.logVideoState(video, 'PAUSED'));
      video.addEventListener('ended', () => this.logVideoState(video, 'ENDED'));
      video.addEventListener('error', (e) => this.logVideoState(video, 'ERROR', e));
      video.addEventListener('stalled', () => this.logVideoState(video, 'STALLED'));
      video.addEventListener('waiting', () => this.logVideoState(video, 'WAITING'));

      // Handle autoplay with muted attribute for browser compatibility
      if (video.hasAttribute('autoplay')) {
        video.muted = true;
        this.logVideoState(video, 'AUTOPLAY ENABLED');

        // Add a small delay before playing to avoid conflicts
        setTimeout(() => {
          this.logVideoState(video, 'ATTEMPTING AUTOPLAY');
          this.safePlayVideo(video);
        }, 100);
      }
    });

    // Handle visibility changes for performance
    if ('IntersectionObserver' in window) {
      this.logVideoState(null, 'INTERSECTION OBSERVER INITIALIZED');

      const videoObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const video = entry.target;

          if (entry.isIntersecting) {
            // Video is visible
            this.logVideoState(video, 'VISIBLE IN VIEWPORT');
            if (video.hasAttribute('autoplay') && video.paused) {
              this.logVideoState(video, 'AUTOPLAY TRIGGERED BY VISIBILITY');
              // Add a small delay before playing to avoid conflicts
              setTimeout(() => {
                this.safePlayVideo(video);
              }, 100);
            }
          } else {
            // Video is not visible
            this.logVideoState(video, 'HIDDEN FROM VIEWPORT');
            this.safePauseVideo(video);
          }
        });
      }, { threshold: 0.25 });

      // Observe all videos
      this.videos.forEach(video => {
        this.logVideoState(video, 'ADDED TO INTERSECTION OBSERVER');
        videoObserver.observe(video);
      });
    } else {
      this.logVideoState(null, 'INTERSECTION OBSERVER NOT SUPPORTED');
    }
  }

  /**
   * Safely play a video and track the promise
   */
  safePlayVideo(video) {
    // Don't try to play if already playing
    if (!video.paused) {
      this.logVideoState(video, 'PLAY SKIPPED - ALREADY PLAYING');
      return;
    }

    // Don't try to play if there's a pending play operation
    if (this.playPromises.get(video)) {
      this.logVideoState(video, 'PLAY SKIPPED - OPERATION PENDING');
      return;
    }

    try {
      this.logVideoState(video, 'PLAY REQUESTED');
      const playPromise = video.play();

      if (playPromise !== undefined) {
        // Store the promise
        this.playPromises.set(video, playPromise);
        this.logVideoState(video, 'PLAY PROMISE STORED');

        // Clear the promise reference when it resolves or rejects
        playPromise
          .then(() => {
            this.playPromises.delete(video);
            this.logVideoState(video, 'PLAY PROMISE RESOLVED');
          })
          .catch(error => {
            this.playPromises.delete(video);
            this.logVideoState(video, 'PLAY PROMISE REJECTED', error);
          });
      } else {
        this.logVideoState(video, 'PLAY PROMISE UNDEFINED');
      }
    } catch (error) {
      this.logVideoState(video, 'PLAY ERROR', error);
    }
  }

  /**
   * Safely pause a video, respecting any pending play operation
   */
  safePauseVideo(video) {
    // If there's no pending play operation, we can safely pause
    if (!this.playPromises.has(video)) {
      if (!video.paused) {
        this.logVideoState(video, 'PAUSE REQUESTED - NO PENDING PLAY');
        video.pause();
      } else {
        this.logVideoState(video, 'PAUSE SKIPPED - ALREADY PAUSED');
      }
      return;
    }

    // If there is a pending play operation, wait for it to complete before pausing
    this.logVideoState(video, 'PAUSE WAITING FOR PENDING PLAY');
    const playPromise = this.playPromises.get(video);
    playPromise
      .then(() => {
        // Now it's safe to pause
        if (!video.paused) {
          this.logVideoState(video, 'PAUSE AFTER PLAY COMPLETED');
          video.pause();
        } else {
          this.logVideoState(video, 'PAUSE SKIPPED - ALREADY PAUSED AFTER PLAY');
        }
        this.playPromises.delete(video);
      })
      .catch((error) => {
        // Promise rejected, clean up
        this.logVideoState(video, 'PAUSE AFTER PLAY REJECTED', error);
        this.playPromises.delete(video);
      });
  }

  /**
   * Set up event listeners
   */
  setupEventListeners() {
    // Handle window resize for responsive behavior
    this.boundHandleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.boundHandleResize);

    if (window.visualViewport) {
      this.boundVisualViewportResize = this.handleResize.bind(this);
      window.visualViewport.addEventListener('resize', this.boundVisualViewportResize);
    }

    // Initial resize handling
    this.handleResize();

    // Videos are set to autoplay by default

    // Handle page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Page is hidden, pause videos
        this.videos.forEach(video => {
          this.safePauseVideo(video);
        });
      } else {
        // Page is visible, play videos with autoplay
        this.videos.forEach(video => {
          if (video.hasAttribute('autoplay') && video.paused) {
            // Add a small delay before playing to avoid conflicts
            setTimeout(() => {
              this.safePlayVideo(video);
            }, 100);
          }
        });
      }
    });
  }

  /**
   * Handle window resize
   */
  handleResize() {
    const viewportWidth = this.getViewportWidth();
    const isMobile = viewportWidth < 768;

    if (this.isMobileViewport === isMobile) {
      return;
    }

    this.isMobileViewport = isMobile;

    if (this.videoContainers.length) {
      this.videoContainers.forEach((container) => {
        container.style.display = isMobile ? 'block' : 'none';
      });
    }

    // Pause videos that are not visible when moving to desktop/tablet view
    if (!isMobile) {
      this.videos.forEach((video) => {
        this.safePauseVideo(video);
      });
    }
  }

  /**
   * Calculate the most reliable viewport width across browsers and in-app webviews
   */
  getViewportWidth() {
    const candidates = [
      window.visualViewport && window.visualViewport.width,
      window.innerWidth,
      document.documentElement && document.documentElement.clientWidth,
      window.screen && window.screen.width,
    ].filter((value) => typeof value === 'number' && !Number.isNaN(value));

    if (!candidates.length) {
      return window.innerWidth;
    }

    return Math.min(...candidates);
  }

  disconnectedCallback() {
    if (this.boundHandleResize) {
      window.removeEventListener('resize', this.boundHandleResize);
    }

    if (this.boundVisualViewportResize && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.boundVisualViewportResize);
    }
  }

  /**
   * Update the video state indicator (debug only, no visual indicators)
   */
  updateVideoStateIndicator(video, state) {
    // Only log to console in debug mode, no visual indicators
    if (this.debugMode && video) {
      const videoId = video.id || 'unnamed-video';
    }
  }

  /**
   * Update a global state indicator (debug only, no visual indicators)
   */
  updateGlobalStateIndicator(state) {
    // Only log to console in debug mode, no visual indicators
    if (this.debugMode) {
      console.error(`Global: ${state}`);
    }
  }
}

// Register the custom element
customElements.define('product-media-showcase', ProductMediaShowcase);

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Find all product media showcase sections
  const showcaseSections = document.querySelectorAll('.product-media-showcase');

  // Initialize each section
  showcaseSections.forEach(section => {
    if (!(section instanceof ProductMediaShowcase)) {
      // Upgrade the element to a ProductMediaShowcase if it's not already
      Object.setPrototypeOf(section, ProductMediaShowcase.prototype);
      ProductMediaShowcase.call(section);
    }
  });
});

